import React, { useEffect, useState } from "react";
import { inject, observer } from "mobx-react";

import { makeStyles } from "@material-ui/core";

import Divider from "@material-ui/core/Divider";
import TextField from "@material-ui/core/TextField";
import Autocomplete from "@material-ui/lab/Autocomplete";
import CircularProgress from "@material-ui/core/CircularProgress";

import DeleteOutlinedIcon from "@material-ui/icons/DeleteOutlined";
import EditOutlinedIcon from "@material-ui/icons/EditOutlined";

import StyledIconButton from "../../buttons/StyledIconButton";
import StyledButton from "../../buttons/StyledButton";

import { debounce, cx } from "../../lib/utils";
import { FIELD_TYPES } from "app/Chat/lib/constants";

const useStyle = makeStyles((theme) => ({
  title: {
    fontSize: 14,
    fontWeight: "normal",
  },
  heading__container: {
    paddingTop: "0.75rem",
  },
  heading: {
    fontSize: 14,
    fontWeight: "normal",
  },
  textMuted: {
    color: "#818181",
  },
  textError: {
    color: "red",
  },
  container: {
    padding: "1rem",
  },
  list__container: {
    listStyleType: "none",
    padding: 0,
  },
  list__item: {
    display: "flex",
    flexDirection: "row",
    alignItems: "center",
    gap: ".5rem",
    justifyContent: "space-between",
    padding: "0.5rem 0",

    "&.deleted *": {
      textDecoration: "line-through !important",
    },
  },
  formContainer: {
    display: "flex",
    flexDirection: "row",
    alignItems: "center",
    gap: ".5rem",
    margin: "0.5rem 0",
  },
  formControl: {
    minWidth: 120,
  },
  action__content: {},
  action__container: {
    display: "flex",
    flexDirection: "row",
    alignItems: "center",
    justifyContent: "space-between",
  },
  actions: {
    display: "flex",
    flexDirection: "row",
    alignItems: "center",
    gap: ".5rem",
  },
}));

const LookupSelector = (props) => {
  const {
    params,
    onCancel,
    onSubmit,
    //
    sheets,
    fetchSheets,
    setSheets,
    fetchListConfig,
    setListConfigs,
  } = props;

  const classes = useStyle();

  const [data, setData] = useState([]);
  const [errorMessage, setErrorMessage] = useState("");
  const [listConfigs, setLocalListConfigs] = useState({});
  const [isSubmitting, setIsSubmitting] = useState(false);

  const handleLoadInitialData = async () => {
    try {
      const res = await fetchSheets();
      if (res && res.data) {
        setSheets(res.data);
      }
    } catch (e) {
      console.log(`e->`, e);
      setErrorMessage(e.message);
    }
  };

  const getSheetInfo = async ({ list_id }) => {
    try {
      const res = await fetchListConfig({ list_id });
      if (res && res.data) {
        setLocalListConfigs((prevListConfigs) => {
          const newListConfigs = { ...prevListConfigs };
          newListConfigs[list_id] = res.data;
          return newListConfigs;
        });

        setListConfigs(list_id, res.data);
      }
    } catch (e) {
      console.log(`e->`, e);
      setErrorMessage(e.message);
    }
  };

  const handleSheetNameChange = (i, j) => (e, newValue) => {
    setErrorMessage("");
    const value = newValue
      ? { sheetId: newValue.id, sheetName: newValue.name }
      : { sheetId: undefined, sheetName: undefined };

    setData((prevData) => {
      const newData = [...prevData];
      if (!newData[i]) {
        newData[i] = [];
      }
      newData[i][j] = value;
      return newData;
    });

    if (!value.sheetId) return;

    if (!listConfigs || !listConfigs[value.sheetId]) {
      debounce(async (params) => {
        await getSheetInfo(params);
      })({ list_id: value.sheetId });
    }
  };

  const handleColumnNameChange = (i, j) => (e, newValue) => {
    setErrorMessage("");

    const value = newValue
      ? { columnId: newValue.id, columnName: newValue.name }
      : { columnId: undefined, columnName: undefined };

    setData((prevData) => {
      const newData = [...prevData];
      if (!newData[i]) {
        newData[i] = [];
      }
      newData[i][j].columnId = value.columnId;
      newData[i][j].columnName = value.columnName;
      return newData;
    });
  };

  const handleDeleteField = (i, j) => () => {
    setData((prevData) => {
      const newData = [...prevData];
      if (!newData[i]) {
        newData[i] = [];
      }

      newData[i][j] = { ...newData[i][j], isDeleted: true };
      return newData;
    });
  };

  const handleEnableField = (i, j) => () => {
    setData((prevData) => {
      const newData = [...prevData];
      if (!newData[i]) {
        newData[i] = [];
      }

      newData[i][j] = { ...newData[i][j], isDeleted: false };
      return newData;
    });
  };

  const handleCancel = () => {
    onCancel();
  };

  const handleSubmit = async () => {
    try {
      setIsSubmitting(true);
      setErrorMessage("");

      const finalData = [];

      for (let i = 0; i < params.length; i++) {
        const _fields = [];

        inner: for (let j = 0; j < params[i].fields.length; j++) {
          if (!data[i]) {
            throw new Error(
              `Please select the sheet name for ${params[i].fields[j].field_name} field`
            );
          }

          if (!data[i][j]) {
            throw new Error(
              `Please select the column name for ${params[i].fields[j].field_name} field`
            );
          }

          const { isDeleted, sheetId, columnId } = data[i][j];

          if (isDeleted) continue inner;

          if (!sheetId) {
            throw new Error(
              `Please select the sheet name for ${params[i].fields[j].field_name} field`
            );
          }

          if (!columnId) {
            throw new Error(
              `Please select the column name for ${params[i].fields[j].field_name}`
            );
          }

          _fields.push({
            field_name: params[i].fields[j].field_name,
            field_type_id: FIELD_TYPES.lookup,
            //TODO: add inside attributes
            attributes: {
              source_list_id: sheetId,
              source_column_id: columnId,
            },
          });
        }
        if (_fields.length)
          finalData.push({
            sheetId: params[i].sheetId,
            sheetName: params[i].sheetName,
            fields: _fields,
            shouldCreateDummyData: params[i].shouldCreateDummyData,
          });
      }

      // const finalData = data.filter(
      //   (fields) => fields.filter((field) => !field.isDeleted).length > 0
      // );

      if (!finalData.length) {
        throw new Error("No fields to save");
      }

      await onSubmit(finalData);
    } catch (e) {
      setErrorMessage(e.message);
      setIsSubmitting(false);
    }
  };

  const renderColumnPicker = (i, j, field) => {
    if (!data[i] || !data[i][j] || !data[i][j]?.sheetId) return null;
    const sheetId = data[i][j]?.sheetId;
    const listConfig = listConfigs[sheetId];

    if (!listConfig)
      return (
        <div
          style={{
            display: "flex",
            justifyContent: "center",
            alignItems: "center",
          }}
        >
          <CircularProgress size={20} />
        </div>
      );

    const { listData } = listConfig;

    if (!listData) return null;
    const columnFieldName = `${field.field_name}.column.lookup`;
    const isDeleted = Boolean(data[i][j]?.isDeleted);

    const value = {
      name: data[i]?.[j]?.columnName || "",
      id: data[i]?.[j]?.columnId || "",
    };

    return (
      <Autocomplete
        id={columnFieldName}
        className={classes.formControl}
        disabled={isDeleted}
        options={listData}
        value={value}
        onChange={handleColumnNameChange(i, j)}
        getOptionLabel={(option) => option.name}
        renderInput={(params) => (
          <TextField {...params} label="Column" variant="outlined" />
        )}
      />
    );
  };

  const renderLookupSelector = () => {
    if (!sheets || !sheets.length) {
      return (
        <div className={classes.container}>
          <CircularProgress size={14} />
        </div>
      );
    }

    return (
      <div className={classes.container}>
        <h4
          className={classes.title}
          style={{ fontSize: 14, fontWeight: "normal" }}
        >
          Seems like some fields need to be manually configured since we didn't
          find the matching sheet or field.
        </h4>
        {params.map((param, i) => {
          return (
            <div className={classes.action__content} key={i}>
              {i < params.length - 1 && <Divider />}
              <div className={classes.heading__container}>
                <h5 className={classes.heading}>
                  {param.sheetName}
                  <span className={classes.textMuted}> sheet has</span>
                </h5>
              </div>
              <ul className={classes.list__container}>
                {param.fields.map((field, j) => {
                  const sheetFieldName = `${field.field_name}.sheet.lookup`;
                  const isDeleted = Boolean(data?.[i]?.[j]?.isDeleted);

                  const value = {
                    id: data?.[i]?.[j]?.sheetId || "",
                    name: data?.[i]?.[j]?.sheetName || "",
                  };

                  return (
                    <li key={j} className={cx(classes.list__item, isDeleted && "deleted")}>
                      <div
                        style={{
                          display: "flex",
                          flexDirection: "column",
                          width: "100%",
                        }}
                      >
                        <div>
                          {field.field_name}{" "}
                          <span className={classes.textMuted}> field is related to:</span>
                        </div>
                        <div className={classes.formContainer}>
                          <Autocomplete
                            id={sheetFieldName}
                            className={classes.formControl}
                            disabled={isDeleted}
                            options={sheets}
                            value={value}
                            onChange={handleSheetNameChange(i, j)}
                            getOptionLabel={(option) => option.name}
                            getOptionSelected={(option) => option.id === value.id}
                            style={{
                              width: "50%",
                            }}
                            renderInput={(params) => (
                              <TextField {...params} label="Sheet" variant="outlined" />
                            )}
                          />
                          <div
                            style={{
                              width: "50%",
                            }}
                          >
                            {renderColumnPicker(i, j, field)}
                          </div>
                          <div>
                            {isDeleted ? (
                              <StyledIconButton
                                edge="end"
                                aria-label="edit"
                                onClick={handleEnableField(i, j)}
                              >
                                <EditOutlinedIcon fontSize="small" />
                              </StyledIconButton>
                            ) : (
                              <StyledIconButton
                                edge="end"
                                aria-label="delete"
                                onClick={handleDeleteField(i, j)}
                              >
                                <DeleteOutlinedIcon fontSize="small" />
                              </StyledIconButton>
                            )}
                          </div>
                        </div>
                      </div>
                    </li>
                  );
                })}
              </ul>
            </div>
          );
        })}

        <div className={classes.action__container}>
          <div>
            {errorMessage && (
              <p className={classes.textError}>{errorMessage}</p>
            )}
          </div>
          <div className={classes.actions}>
            <StyledButton disabled={isSubmitting} onClick={handleCancel}>
              Cancel
            </StyledButton>
            <StyledButton
              disabled={isSubmitting}
              color="primary"
              onClick={handleSubmit}
            >
              {isSubmitting ? "Loading..." : "Continue"}
            </StyledButton>
          </div>
        </div>
      </div>
    );
  };

  useEffect(() => {
    handleLoadInitialData();
  }, []);

  return renderLookupSelector();
};

export default inject(({ store }) => {
  return {
    sheets: store.assistantStore.sheets,
    getListConfig: store.assistantStore.getListConfig,
    fetchSheets: store.assistantStore.fetchSheets,
    setSheets: store.assistantStore.setSheets,
    fetchListConfig: store.assistantStore.fetchListConfig,
    setListConfigs: store.assistantStore.setListConfigs,
  };
})(observer(LookupSelector));
